// frontend/src/features/portfolio-diversification-optimizer-pro/components/StressTestingPanel.tsx
//
// Pillar 4 -- Tail Risk, Cornish-Fisher VaR & Crisis Replays. Surfaces
// higher-moment fat-tail risk (skewness, excess kurtosis, Ulcer Index, Sortino
// downside deviation), compares Historical VaR vs. Cornish-Fisher VaR and
// 95%/99% Expected Shortfall (CVaR), models covariance-driven hypothetical
// macro shocks, and replays the current portfolio across historical crisis
// regimes.
//
// Units note: all VaR/CVaR/vol/return/drawdown fields are FRACTIONS (0..1) and
// are multiplied by 100 for display; skewness/kurtosis are raw numbers.
//
// Themed for the shared light/dark dashboard shell.

import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CalendarClock, Gauge, History, Zap } from "lucide-react";

import type { StressTestAnalysis } from "../types";

interface StressTestingPanelProps {
  stressTesting: StressTestAnalysis;
}

const CHART_GRID = "#e2e8f0";
const CHART_AXIS = "#94a3b8";
const TOOLTIP_STYLE: React.CSSProperties = {
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  fontSize: 12,
  background: "rgba(255,255,255,0.97)",
  color: "#0f172a",
};

const kpiCardClass =
  "rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900/90";
const cardClass =
  "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/90";

export const StressTestingPanel: React.FC<StressTestingPanelProps> = ({ stressTesting }) => {
  // Data for the Tail Loss Escalation Recharts BarChart.
  const tailEscalationData = useMemo(
    () => [
      { metric: "95% Hist VaR", lossPct: Number((stressTesting.var_95_daily * 100).toFixed(2)), color: "#38bdf8" },
      {
        metric: "95% Cornish-Fisher",
        lossPct: Number((stressTesting.cornish_fisher_var_95_daily * 100).toFixed(2)),
        color: "#818cf8",
      },
      { metric: "95% CVaR (ES)", lossPct: Number((stressTesting.cvar_95_daily * 100).toFixed(2)), color: "#f59e0b" },
      { metric: "99% Hist VaR", lossPct: Number((stressTesting.var_99_daily * 100).toFixed(2)), color: "#fb7185" },
      { metric: "99% Extreme CVaR", lossPct: Number((stressTesting.cvar_99_daily * 100).toFixed(2)), color: "#e11d48" },
    ],
    [stressTesting]
  );

  const fatTailRatio =
    stressTesting.var_95_daily > 0
      ? stressTesting.cornish_fisher_var_95_daily / stressTesting.var_95_daily
      : 1.0;

  return (
    <div className="space-y-6">
      {/* Top 6 Tail Risk & Higher-Moment KPI Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        <div className={kpiCardClass}>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">Daily 95% Hist VaR</div>
          <div className="mt-1 font-mono text-xl font-bold text-sky-600 dark:text-sky-400">
            -{(stressTesting.var_95_daily * 100).toFixed(2)}%
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">1-in-20 trading days</div>
        </div>

        <div className={kpiCardClass}>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">Cornish-Fisher 95% VaR</div>
          <div className="mt-1 font-mono text-xl font-bold text-indigo-600 dark:text-indigo-400">
            -{(stressTesting.cornish_fisher_var_95_daily * 100).toFixed(2)}%
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Skew/Kurtosis ratio:{" "}
            <strong className="font-mono text-slate-700 dark:text-slate-200">{fatTailRatio.toFixed(2)}x</strong>
          </div>
        </div>

        <div className={kpiCardClass}>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">Daily 95% CVaR (ES)</div>
          <div className="mt-1 font-mono text-xl font-bold text-amber-600 dark:text-amber-400">
            -{(stressTesting.cvar_95_daily * 100).toFixed(2)}%
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">Expected tail shortfall</div>
        </div>

        <div className={kpiCardClass}>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">Daily 99% Extreme CVaR</div>
          <div className="mt-1 font-mono text-xl font-bold text-rose-600 dark:text-rose-400">
            -{(stressTesting.cvar_99_daily * 100).toFixed(2)}%
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">Worst 1% crash average</div>
        </div>

        <div className={kpiCardClass}>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">Ulcer Index &amp; Sortino Vol</div>
          <div className="mt-1 font-mono text-xl font-bold text-slate-900 dark:text-white">
            {(stressTesting.ulcer_index * 100).toFixed(2)}%
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Downside Dev:{" "}
            <strong className="font-mono text-slate-700 dark:text-slate-200">
              {(stressTesting.annualized_downside_deviation * 100).toFixed(1)}%
            </strong>
          </div>
        </div>

        <div className={kpiCardClass}>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">Skewness / Ex. Kurtosis</div>
          <div className="mt-1 font-mono text-xl font-bold text-slate-900 dark:text-white">
            {stressTesting.skewness.toFixed(2)} / {stressTesting.excess_kurtosis.toFixed(2)}
          </div>
          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            {stressTesting.skewness < -0.25 ? "Negative crash skew" : "Symmetric tail profile"}
          </div>
        </div>
      </div>

      {/* Middle Row: Tail Escalation Chart + Hypothetical Macro Shocks */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left: Daily Tail Loss Escalation Profile */}
        <div className={`${cardClass} lg:col-span-5`}>
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
              <Gauge className="h-4 w-4 text-rose-500 dark:text-rose-400" />
              Daily Tail Loss Escalation Profile
            </h3>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Compares 95% Historical VaR, Higher-Moment Cornish-Fisher VaR, and 95%/99% Expected Shortfall (CVaR).
            </p>
          </div>

          <div className="mt-4 h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={tailEscalationData} margin={{ top: 12, right: 16, left: 0, bottom: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} strokeOpacity={0.4} />
                <XAxis
                  dataKey="metric"
                  stroke={CHART_AXIS}
                  tick={{ fontSize: 10, fill: CHART_AXIS }}
                  interval={0}
                  angle={-12}
                  textAnchor="end"
                />
                <YAxis unit="%" stroke={CHART_AXIS} tick={{ fontSize: 11, fill: CHART_AXIS }} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value: number) => [`-${value.toFixed(2)}%`, "Daily Loss Magnitude"]}
                />
                <Bar dataKey="lossPct" radius={[6, 6, 0, 0]}>
                  {tailEscalationData.map((entry, idx) => (
                    <Cell key={`tail-cell-${idx}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right: Covariance-Driven Hypothetical Shock Scenarios */}
        <div className={`${cardClass} lg:col-span-7`}>
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
              <Zap className="h-4 w-4 text-amber-500 dark:text-amber-400" />
              Covariance-Driven Hypothetical Macro &amp; Contagion Shocks
            </h3>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Forward-looking stress tests scaled by each holding's PC1 systemic loading and covariance structure.
            </p>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            {stressTesting.hypothetical_shocks.map((shock) => {
              const retPct = shock.estimated_portfolio_return * 100;
              const volSpikePct = shock.estimated_volatility_spike * 100;
              const worstLossPct = shock.worst_impact_Asset_loss * 100;

              return (
                <div
                  key={shock.scenario_id}
                  className="flex flex-col justify-between rounded-xl border border-slate-200 bg-slate-50/60 p-4 transition hover:border-slate-300 dark:border-slate-800 dark:bg-slate-950/60 dark:hover:border-slate-700"
                >
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-xs font-semibold text-slate-800 dark:text-white">{shock.scenario_name}</h4>
                      <span className="shrink-0 rounded-md bg-rose-500/15 px-2 py-0.5 font-mono text-xs font-bold text-rose-600 ring-1 ring-rose-500/30 dark:text-rose-400">
                        {retPct >= 0 ? "+" : ""}
                        {retPct.toFixed(1)}%
                      </span>
                    </div>
                    <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                      {shock.description}
                    </p>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-200/80 pt-3 text-[11px] dark:border-slate-800/80">
                    <div>
                      <span className="text-slate-400 dark:text-slate-500">Stressed Vol</span>
                      <div className="font-mono font-semibold text-amber-600 dark:text-amber-300">
                        {volSpikePct.toFixed(1)}%
                      </div>
                    </div>
                    <div>
                      <span className="text-slate-400 dark:text-slate-500">Worst Drag</span>
                      <div className="font-mono font-semibold text-rose-600 dark:text-rose-300">
                        {shock.worst_impact_ticker} ({worstLossPct.toFixed(0)}%)
                      </div>
                    </div>
                    <div>
                      <span className="text-slate-400 dark:text-slate-500">Best Buffer</span>
                      <div className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                        {shock.best_stabilizer_ticker}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom Card: Historical Crisis Regime Replays */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900/90">
        <div className="flex flex-col gap-2 border-b border-slate-200 pb-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-white">
              <History className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
              Historical Crisis Regime Replays &amp; Lookback Worst-Window Audit
            </h3>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Exact replay of your current portfolio weights across historical market stress episodes overlapped by your lookback window.
            </p>
          </div>
          <span className="shrink-0 rounded-lg bg-slate-100 px-3 py-1 font-mono text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Full-Sample Max DD: {(stressTesting.max_drawdown * 100).toFixed(2)}%
          </span>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-slate-200 bg-slate-50/70 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:border-slate-800 dark:bg-slate-950/70 dark:text-slate-400">
              <tr>
                <th className="px-3 py-3">Stress Regime / Episode</th>
                <th className="px-3 py-3">Date Window</th>
                <th className="px-3 py-3">Sessions</th>
                <th className="px-3 py-3">Cumulative Return</th>
                <th className="px-3 py-3">Max Drawdown</th>
                <th className="px-3 py-3">Worst Single Day</th>
                <th className="px-3 py-3">Realized Ann. Vol</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200/70 dark:divide-slate-800/60">
              {stressTesting.historical_replays.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-slate-400 dark:text-slate-500">
                    No historical crisis windows overlapped the selected lookback horizon.
                  </td>
                </tr>
              ) : (
                stressTesting.historical_replays.map((rep) => {
                  const cumRetPct = rep.portfolio_Cumulative_return * 100;
                  const maxDdPct = rep.max_drawdown * 100;
                  const worstDayPct = rep.worst_single_day * 100;
                  const rVolPct = rep.realized_annualized_vol * 100;

                  return (
                    <tr key={rep.regime_id} className="transition hover:bg-slate-100/60 dark:hover:bg-slate-800/40">
                      <td className="px-3 py-3 font-semibold text-slate-800 dark:text-white">
                        <div className="flex items-center gap-2">
                          <CalendarClock className="h-3.5 w-3.5 shrink-0 text-indigo-500 dark:text-indigo-400" />
                          <span>{rep.regime_name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 font-mono text-slate-500 dark:text-slate-400">
                        {rep.start_date} → {rep.end_date}
                      </td>
                      <td className="px-3 py-3 font-mono text-slate-600 dark:text-slate-300">{rep.trading_days}d</td>
                      <td className="px-3 py-3">
                        <span
                          className={`font-mono font-bold ${
                            cumRetPct >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"
                          }`}
                        >
                          {cumRetPct >= 0 ? "+" : ""}
                          {cumRetPct.toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="h-2 w-20 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                            <div
                              className="h-full rounded-full bg-rose-500"
                              style={{ width: `${Math.min(100, Math.abs(maxDdPct) * 2)}%` }}
                            />
                          </div>
                          <span className="font-mono font-semibold text-rose-600 dark:text-rose-300">
                            {maxDdPct.toFixed(2)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 font-mono text-amber-600 dark:text-amber-300">
                        {worstDayPct.toFixed(2)}%
                      </td>
                      <td className="px-3 py-3 font-mono text-slate-700 dark:text-slate-200">{rVolPct.toFixed(2)}%</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
